import styled from 'styled-components';

import { Action, ActionBadge, ActionIconContainer, ActionLabel } from 'components/shared/actionPalette';
import Menu from 'components/shared/menu';
import { Flags } from 'constants/flags';
import { useProfile } from 'context/profile';
import {
  useMarkAllNotificationsRead,
  useNotificationUnreadCount,
  useUpdateNotification,
} from 'services/api/notification';
import { colors } from 'styles/theme/colors';
import BellIcon from 'svg/BellIcon';

import NotificationInbox from './NotificationInbox';

// The unread badge shows the global unread count, capped at "99+" (§5 item 1).
function formatBadgeCount(count: number): string {
  return count > 99 ? '99+' : String(count);
}

export default function NotificationBell() {
  const { profile } = useProfile();
  const hasFlag = profile.hasFlag(Flags.InAppNotifications);

  // `enabled` on the flag: no request fires when the flag is off (the early return below also
  // hides the bell entirely, so the surface is flag-gated end to end — §5 AC).
  const { data: unreadCount = 0 } = useNotificationUnreadCount({ enabled: hasFlag });

  // Owned here (not in the panel): opening a row closes the panel, and a mutation observer that
  // unmounts mid-flight would drop its onSuccess invalidation. The bell stays mounted across
  // in-app navigation (it lives in the persistent UserBar), so the invalidation always lands.
  const updateNotification = useUpdateNotification();
  const markAllRead = useMarkAllNotificationsRead();

  if (!hasFlag) return null;

  return (
    <Menu>
      <Menu.Trigger>
        <Action aria-label='Notifications' title='Notifications' data-cy='notificationsBell'>
          <ActionIconContainer $count={unreadCount}>
            <BellIcon color={colors.white} width={18} height={18} />
            {unreadCount > 0 && <BellBadge data-testid='notificationsBadge'>{formatBadgeCount(unreadCount)}</BellBadge>}
          </ActionIconContainer>
          <ActionLabel>Notifications</ActionLabel>
        </Action>
      </Menu.Trigger>
      <Menu.Content position='left' style={{ width: 400, maxWidth: 400, padding: 0, overflow: 'hidden' }}>
        <NotificationInbox enabled={hasFlag} updateNotification={updateNotification} markAllRead={markAllRead} />
      </Menu.Content>
    </Menu>
  );
}

// The shared ActionBadge is a fixed 14px circle (single-digit task counts); the unread badge must
// also fit the "99+" cap, so it grows into a pill for multi-character values.
const BellBadge = styled(ActionBadge)`
  border-radius: 8px;
  min-width: 14px;
  padding: 0 3px;
  width: auto;
`;
